import { useContext } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { AuthContext } from './auth-context';
import type { AuthContextValue, UserProfile } from './types';

/**
 * useAuth - canonical hook for reading auth state and triggering auth actions.
 * Components route on `auth.status` (the four-state discriminator) rather than
 * inspecting user/session/profile directly.
 *
 * Throws if used outside an AuthProvider. This is intentional: a hook call
 * outside the provider tree is a programming error, not a runtime condition
 * the UI should try to recover from.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}

/**
 * useReadyAuth - convenience hook for components that should only render once
 * auth is fully resolved (Supabase session + active user_profiles row loaded).
 *
 * Throws if auth.status !== 'ready'. Components that may render in earlier
 * states (loading, unauthenticated, unprovisioned) should use useAuth and
 * branch on status; only call useReadyAuth from inside the AppShell tree where
 * RequireAuth has already gated.
 */
export function useReadyAuth(): {
  user: User;
  session: Session;
  profile: UserProfile;
} {
  const { auth } = useAuth();
  if (auth.status !== 'ready') {
    throw new Error('useReadyAuth called outside ready state');
  }
  return { user: auth.user, session: auth.session, profile: auth.profile };
}
